import React, { useState, useEffect, useRef } from 'react';
import { speakersService } from '../../services/speakers';
import { notificationService } from '../../services/notifications';
import { AudioFile, Speaker, Transcript, EditHistoryHook } from '../../types';
import { ClockIcon, AddUserIcon } from '../../icons';
import SidebarCollapseButton from '../ui/SidebarCollapseButton';
import TranscriptMeta from './TranscriptMeta';
import SpeakerSection from './SpeakerSection';
import ExportOptions from './ExportOptions';
import EditHistoryModal from './EditHistoryModal';
import TutorialManager from '../tutorial/TutorialManager';
import { transcriptToolsTutorial } from '../tutorial/tutorials/TranscriptToolsTutorial';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { useTutorial } from '../../hooks/useTutorial';

interface TranscriptToolsSidebarProps {
  onCollapse: () => void;
  transcript?: Transcript;
  audioFile?: AudioFile;
  audioFileId?: string;
  editHistoryHook: EditHistoryHook;
  onSpeakerUpdated?: () => void;
  onTranscriptUpdated?: (updatedTranscript: Transcript) => void;
}

const TranscriptToolsSidebar: React.FC<TranscriptToolsSidebarProps> = ({
  onCollapse,
  transcript,
  audioFile,
  audioFileId,
  editHistoryHook,
  onSpeakerUpdated,
  onTranscriptUpdated,
}) => {
  const [speakers, setSpeakers] = useState<Speaker[]>(
    transcript?.speakers || []
  );
  const [currentTranscript, setCurrentTranscript] = useState<Transcript | null>(
    transcript || null
  );
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isTutorialShown } = useTutorialContext();

  useEffect(() => {
    if (transcript?.speakers) {
      setSpeakers(transcript.speakers);
    }
    if (transcript) {
      setCurrentTranscript(transcript);
    }
  }, [transcript]);

  // Handle scroll event
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setHasScrolled(true);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Trigger transcript tools tutorial when scrolled
  useTutorial({
    tutorialId: 'transcript-tools',
    trigger:
      hasScrolled &&
      !!audioFile?.hasTranscript &&
      isTutorialShown('transcript-editing'),
    delay: 0,
  });

  const handleSpeakerUpdate = (updatedSpeaker: Speaker) => {
    setSpeakers((prev) =>
      prev.map((speaker) =>
        speaker.id === updatedSpeaker.id ? updatedSpeaker : speaker
      )
    );
  };

  const handleCreateSpeaker = async () => {
    if (!audioFileId) return;

    try {
      const newSpeaker = await speakersService.createSpeaker(audioFileId);
      setSpeakers((prev) => [...prev, newSpeaker]);
      onSpeakerUpdated?.();

      notificationService.success(
        `Speaker "${newSpeaker.originalLabel}" created successfully!`
      );
    } catch (error) {
      console.error('Failed to create speaker:', error);
      notificationService.error('Failed to create speaker. Please try again.');
    }
  };

  const handleSpeakerDelete = (speakerId: string) => {
    setSpeakers((prev) => prev.filter((speaker) => speaker.id !== speakerId));
  };

  const handleTranscriptUpdate = (updatedTranscript: Transcript) => {
    setCurrentTranscript(updatedTranscript);
    onTranscriptUpdated?.(updatedTranscript);
  };

  // Only show Speakers and Export Options if file is ready and has transcript
  const showAdvancedSections =
    audioFile?.status === 'ready' &&
    audioFile?.hasTranscript &&
    currentTranscript;

  return (
    <div className="w-full md:w-96 h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex flex-col">
      <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
          Transcript Tools
        </h3>
        <SidebarCollapseButton
          onClick={onCollapse}
          side="right"
          title="Collapse transcript tools"
        />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-3 md:p-4"
      >
        {!audioFile || !audioFileId ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Select a file to view transcription tools and options.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Meta Section */}
            <div data-tutorial="transcript-meta">
              <TranscriptMeta
                transcript={currentTranscript}
                audioFile={audioFile}
                audioFileId={audioFileId}
                onTranscriptUpdate={handleTranscriptUpdate}
              />
            </div>

            {/* Speakers Section - Only show when file is ready and has transcript */}
            {showAdvancedSections && (
              <div data-tutorial="speakers-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Speakers
                  </h4>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={handleCreateSpeaker}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Add speaker"
                    >
                      <AddUserIcon
                        size={20}
                        className="text-gray-600 dark:text-gray-300"
                      />
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {speakers.map((speaker) => (
                    <SpeakerSection
                      key={speaker.id}
                      speaker={speaker}
                      audioFileId={audioFileId}
                      onSpeakerUpdate={handleSpeakerUpdate}
                      onSpeakerUpdated={onSpeakerUpdated}
                      onSpeakerDelete={handleSpeakerDelete}
                      speakers={speakers}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Edit History Section - Only show when file is ready and has transcript */}
            {showAdvancedSections && (
              <div data-tutorial="edit-history-section">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Edit History
                </h4>
                <button
                  onClick={() => setIsHistoryPanelOpen(true)}
                  className="w-full px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <ClockIcon size={16} />
                  View Edit History
                </button>
              </div>
            )}

            {/* Export Options Section - Only show when file is ready and has transcript */}
            {showAdvancedSections && audioFileId && (
              <div data-tutorial="export-section">
                <ExportOptions audioFileId={audioFileId} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit History Panel Modal */}
      {audioFile?.hasTranscript && audioFileId && (
        <EditHistoryModal
          editHistoryHook={editHistoryHook}
          isOpen={isHistoryPanelOpen}
          onClose={() => setIsHistoryPanelOpen(false)}
        />
      )}

      <TutorialManager tutorials={[transcriptToolsTutorial]} />
    </div>
  );
};

export default TranscriptToolsSidebar;
